import { useState } from "react";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, Users, CheckCircle2, CalendarDays, Printer, ArrowRight, Star, PhoneCall } from "lucide-react";
import { receptionApi, receptionQueryKeys, type ReceptionRow } from "../../shared/api/reception";
import { appointmentsApi, appointmentQueryKeys, assign } from "../../shared/api/appointments";
import { roomsApi, roomQueryKeys, assignRoom, roomLabel, type Room } from "../../shared/api/rooms";
import { useOperatesModule } from "../../shared/hooks/useOperatesModule";
import { meApi } from "../../shared/api/me";
import { DeskDoctorPicker } from "./DeskDoctorPicker";
import { encountersApi } from "../../shared/api/encounters";
import { queueOpsApi } from "../../shared/api/queueOps";
import { branchesApi, branchesQueryKeys, ACTIVE_BRANCH_KEY } from "../../shared/api/branches";
import {
  DOCTOR_REQUIRED_MESSAGE,
  ROOM_REQUIRED_HINT,
  hasDoctor,
  hasRoom,
  startEncounterErrorMessage,
} from "../../shared/encounterStart";
import { useRoomRequired } from "../../shared/hooks/useRoomRequired";
import { apiError } from "../../shared/api/client";
import { formatTime } from "../../shared/format";
import { escapeHtml } from "../../shared/escapeHtml";
import { PatientLink } from "../../shared/ui/PatientLink";
import { ErrorState } from "../../shared/ui/ErrorState";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const STATUS_LABEL: Record<string, string> = {
  REQUESTED: "Chờ xác nhận",
  CONFIRMED: "Đã xác nhận",
  CHECKED_IN: "Đã tiếp đón",
  COMPLETED: "Đã khám xong",
  CANCELLED: "Đã huỷ",
  NO_SHOW: "Không đến",
};

const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "muted" | "outline"
> = {
  REQUESTED: "warning",
  CONFIRMED: "secondary",
  CHECKED_IN: "default",
  COMPLETED: "success",
  CANCELLED: "destructive",
  NO_SHOW: "muted",
};

/** Tiếp đón & Hàng chờ — màn hình quầy lễ tân: check-in cấp số ĐK, gọi vào phòng
 *  cấp số phòng, in phiếu số, bắt đầu khám. */
export function ReceptionPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Ngày đang xem + cờ "chỉ xem ca chưa phân phòng" nằm trên URL (mặc định hôm nay).
  const { filters, set } = useUrlFilters({
    date: param.isoDate(todayIso),
    unassigned: param.bool(),
  });
  const { date, unassigned: onlyUnassigned } = filters;
  const [error, setError] = useState<string | null>(null);

  const queue = useQuery({
    queryKey: receptionQueryKeys.queue(date),
    queryFn: () => receptionApi.queue(date),
    refetchInterval: 20_000,
  });
  const rooms = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
  });
  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: receptionQueryKeys.queue(date) });

  // #19088 — chọn bác sĩ trên dòng cần `queue.coordinate` (Lễ tân, Chủ phòng khám; KHÔNG Bác sĩ).
  // Đang tải hoặc tải hỏng thì coi như không có: API vẫn là chốt chặn thật.
  const profile = useQuery({
    queryKey: ["me", "profile"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });
  const canCoordinate = (profile.data?.permissions ?? []).includes("queue.coordinate");
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  // Chi nhánh đang chọn (giống BranchSwitcher) — cần cho "Gọi bệnh nhân kế tiếp".
  const branchesQuery = useQuery({
    queryKey: branchesQueryKeys.all(),
    queryFn: () => branchesApi.list(),
    staleTime: 5 * 60_000,
  });
  const branchId =
    localStorage.getItem(ACTIVE_BRANCH_KEY) ||
    (branchesQuery.data ?? []).find((b) => b.status === "ACTIVE")?.id ||
    "";
  // #19665 — Phòng = Bắt buộc locks "Bắt đầu khám" until the row has a room.
  const roomRequired = useRoomRequired(branchId || undefined);

  const [callMsg, setCallMsg] = useState<string | null>(null);
  // #18872 — "gọi tiếp" calls a patient INTO A ROOM. Passing null used to match every room, so
  // reception called someone with no room to walk to while the doctor's screen was room-scoped.
  // One room in the branch → no picker and null is fine, the server resolves it (rule 4).
  const branchRooms = (rooms.data ?? []).filter(
    (r) => r.active && (!branchId || r.branchId === branchId)
  );
  const needsRoomChoice = branchRooms.length > 1;
  const [callRoomId, setCallRoomId] = useState("");
  const callNext = useMutation({
    mutationFn: () =>
      queueOpsApi.callNext(branchId, needsRoomChoice ? callRoomId : null),
    onSuccess: (res) => {
      invalidate();
      setCallMsg(
        res.found
          ? `Đã gọi STT ${res.roomNo ?? res.registrationNo ?? "—"}.`
          : "Hiện chưa có bệnh nhân chờ để gọi."
      );
    },
    onError: (e) => setError(apiError(e).detail),
  });
  const setPriority = useMutation({
    mutationFn: (v: { id: string; priority: boolean }) =>
      queueOpsApi.setPriority(v.id, v.priority),
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e).detail),
  });

  const checkIn = useMutation({
    mutationFn: (id: string) => appointmentsApi.transition(id, "CHECKED_IN"),
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e).detail),
  });
  const startEncounter = useMutation({
    mutationFn: (r: ReceptionRow) =>
      encountersApi.createFromAppointment({
        appointmentId: r.appointmentId,
        patientId: r.patientId,
        patientName: r.patientName,
        patientCode: r.patientCode ?? null,
        patientPhone: r.patientPhone,
        patientGender: null,
        patientAge: null,
        // #18899 — doctorId is a UUID server-side. Sending "" made Jackson coerce it to null,
        // so @NotNull fired and the message said "null" while the client had sent an empty
        // string: the two ends were describing different values and the log could not be
        // joined up. A missing doctor stays null, which is what it is.
        doctorId: r.doctorId ?? null,
        doctorName: r.doctorName ?? null,
      }),
    onSuccess: (enc) => navigate(`/encounters/${enc.id}`),
    onError: (e, r) =>
      setError(hasDoctor(r) ? startEncounterErrorMessage(e) : DOCTOR_REQUIRED_MESSAGE),
  });

  const rows = queue.data ?? [];
  // #18605: a future date is view-only — disable every state-changing action (check-in, gọi
  // vào phòng, phân công, bắt đầu khám, gọi kế tiếp, ưu tiên). Past/today keep current logic.
  const isFuture = date > todayIso();
  const waiting = rows.filter((r) => r.status === "CHECKED_IN").length;
  const pending = rows.filter(
    (r) => r.status === "REQUESTED" || r.status === "CONFIRMED"
  ).length;
  const done = rows.filter((r) => r.status === "COMPLETED").length;

  // #18883 — an appointment with no room is now nobody's problem by default. Since #18872 the
  // doctor's list is room-scoped, and an unassigned patient only appears to a holder of
  // `queue.coordinate`; on a one-room branch the server resolves the room itself, so this is a
  // multi-room concern only. Reception is where it has to be visible, because reception is the
  // only screen that can fix it.
  const unassignedRows = needsRoomChoice
    ? rows.filter(
        (r) =>
          !r.roomId &&
          (r.status === "REQUESTED" ||
            r.status === "CONFIRMED" ||
            r.status === "CHECKED_IN")
      )
    : [];
  // Assigning the last one must not leave the desk staring at an empty table it cannot explain.
  const filterUnassigned = onlyUnassigned && unassignedRows.length > 0;
  const visibleRows = filterUnassigned ? unassignedRows : rows;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Tiếp đón &amp; Hàng chờ
          </h1>
          <p className="text-sm text-muted-foreground">
            Tiếp đón bệnh nhân (cấp số đăng ký), gọi vào phòng (cấp số phòng) và bắt đầu
            khám. Đang chờ khám: <strong className="text-foreground">{waiting}</strong>.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {needsRoomChoice && (
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={callRoomId}
              onChange={(e) => setCallRoomId(e.target.value)}
              aria-label="Phòng cần gọi"
            >
              <option value="">— Chọn phòng —</option>
              {branchRooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          <Button
            type="button"
            disabled={
              !branchId ||
              callNext.isPending ||
              isFuture ||
              (needsRoomChoice && !callRoomId)
            }
            onClick={() => {
              setCallMsg(null);
              setError(null);
              callNext.mutate();
            }}
            title="Gọi bệnh nhân đủ điều kiện kế tiếp trong hàng đợi"
          >
            <PhoneCall className="mr-1.5 size-4" />
            {callNext.isPending ? "Đang gọi…" : "Gọi bệnh nhân kế tiếp"}
          </Button>
          <Input
            type="date"
            value={date}
            onChange={(e) => set({ date: e.target.value })}
            className="w-auto"
            aria-label="Chọn ngày"
          />
        </div>
      </div>

      {isFuture && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Đang xem lịch của ngày trong tương lai — chỉ xem. Các thao tác tiếp đón
          (check-in, phân công, gọi vào phòng, bắt đầu khám) đã tạm khóa. Chọn lại
          ngày hôm nay để thao tác.
        </div>
      )}

      {unassignedRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span>
            <b>{unassignedRows.length}</b> bệnh nhân <b>chưa phân phòng</b> — chưa hiện trên
            danh sách khám của phòng nào. Phân phòng để bác sĩ gọi được.
          </span>
          <button
            type="button"
            onClick={() => set({ unassigned: !onlyUnassigned })}
            className="rounded border border-amber-400 px-2 py-0.5 text-xs font-medium hover:bg-amber-100"
          >
            {filterUnassigned ? "Xem tất cả" : `Chỉ xem ${unassignedRows.length} ca này`}
          </button>
        </div>
      )}

      {callMsg && <p className="text-sm text-primary">{callMsg}</p>}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard
          icon={<Clock3 />}
          label="Chờ tiếp đón"
          value={pending}
          hint="Chưa check-in"
        />
        <KpiCard
          icon={<Users />}
          label="Đang chờ khám"
          value={waiting}
          hint="Đã tiếp đón"
        />
        <KpiCard
          icon={<CheckCircle2 />}
          label="Đã khám xong"
          value={done}
          hint="Hoàn thành"
        />
        <KpiCard
          icon={<CalendarDays />}
          label="Tổng trong ngày"
          value={rows.filter((r) => r.status !== "CANCELLED").length}
          hint="Lịch hẹn của ngày đang xem, không gồm lịch đã huỷ"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {/* #19934 — "STT quầy": số của QUẦY TIẾP ĐÓN, khác số vào phòng ở cột Phòng. */}
                <TableHead title="Số thứ tự tại quầy tiếp đón">STT quầy</TableHead>
                {/* #19552 — STT vào phòng (roomNo, #19934); khác STT quầy. */}
                <TableHead title="STT đặt lịch (theo Phạm vi STT)">STT</TableHead>
                <TableHead>Giờ hẹn</TableHead>
                <TableHead>Bệnh nhân</TableHead>
                <TableHead>Bác sĩ</TableHead>
                <TableHead title="Phòng và số thứ tự vào phòng">Phòng · STT vào phòng</TableHead>
                <TableHead>Trạng thái</TableHead>
                <TableHead>Hành động</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    Đang tải…
                  </TableCell>
                </TableRow>
              )}
              {queue.isError && (
                <TableRow>
                  <TableCell colSpan={8} className="py-4">
                    <ErrorState
                      inline
                      error={queue.error}
                      title="Không tải được danh sách tiếp đón"
                      onRetry={() => queue.refetch()}
                    />
                  </TableCell>
                </TableRow>
              )}
              {!queue.isLoading && !queue.isError && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    Không có lịch hẹn nào trong ngày.
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((r) => (
                <TableRow key={r.appointmentId}>
                  <TableCell>
                    {r.registrationNo != null ? (
                      <strong className="text-lg">{r.registrationNo}</strong>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell data-testid="reception-booking-no" className="font-semibold tabular-nums">
                    {r.roomNo ?? "—"}
                  </TableCell>
                  <TableCell>{formatTime(r.startAt)}</TableCell>
                  <TableCell>
                    <PatientLink id={r.patientId} name={r.patientName} className="text-foreground" />
                    <div className="text-xs text-muted-foreground">
                      {r.patientCode ?? ""} {r.patientPhone ? `· ${r.patientPhone}` : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.doctorName ??
                      (r.doctorAssignable && canCoordinate && !isFuture ? (
                        <DeskDoctorPicker
                          row={r}
                          doctors={doctors.data ?? []}
                          date={date}
                          open={pickerFor === r.appointmentId}
                          onOpenChange={(o) => setPickerFor(o ? r.appointmentId : null)}
                          onAssigned={(offDuty) => {
                            setPickerFor(null);
                            invalidate();
                            if (offDuty) {
                              setCallMsg(
                                `Đã gán bác sĩ cho ${r.patientName} — lưu ý: bác sĩ này không trong ca trực của phòng.`
                              );
                            }
                          }}
                          onError={setError}
                        />
                      ) : (
                        "—"
                      ))}
                  </TableCell>
                  <TableCell>
                    {r.roomName ? (
                      // #18988: từ 20/09/2026 có phòng là có số, nên nhánh `: r.roomName` chỉ còn
                      // dành cho DỮ LIỆU CŨ tạo trước thay đổi đó. Giữ nguyên nhánh ấy thay vì
                      // in "undefined": bên trong `${}` một giá trị vắng mặt in ra đúng chữ
                      // "undefined", và `??` chỉ cứu được chỗ nào thực sự viết nó (#18897).
                      r.roomNo != null ? `${r.roomName} · ${r.roomNo}` : r.roomName
                    ) : needsRoomChoice ? (
                      <span className="text-amber-700">Chưa phân phòng</span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={STATUS_VARIANT[r.status] ?? "muted"}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </Badge>
                      {r.queueState === "CALLED" && (
                        <Badge variant="secondary">Đã gọi</Badge>
                      )}
                      {r.queueState === "HELD" && <Badge variant="warning">Tạm giữ</Badge>}
                      {r.queueState === "SKIPPED" && <Badge variant="muted">Bỏ qua</Badge>}
                      {(r.status === "REQUESTED" ||
                        r.status === "CONFIRMED" ||
                        r.status === "CHECKED_IN") && (
                        <button
                          type="button"
                          title={r.priority ? "Bỏ ưu tiên" : "Đánh dấu ưu tiên (gọi trước)"}
                          disabled={setPriority.isPending || isFuture}
                          onClick={() =>
                            setPriority.mutate({
                              id: r.appointmentId,
                              priority: !r.priority,
                            })
                          }
                          className="rounded p-0.5 hover:bg-muted"
                        >
                          <Star
                            size={16}
                            className={
                              r.priority
                                ? "fill-amber-400 text-amber-400"
                                : "text-muted-foreground"
                            }
                          />
                        </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RowActions
                      row={r}
                      rooms={branchRooms}
                      doctors={doctors.data ?? []}
                      disabled={isFuture}
                      roomRequired={roomRequired}
                      onCheckIn={() => checkIn.mutate(r.appointmentId)}
                      onAssigned={invalidate}
                      onStart={() => startEncounter.mutate(r)}
                      onPickDoctor={
                        r.doctorAssignable && canCoordinate && !isFuture
                          ? () => setPickerFor(r.appointmentId)
                          : undefined
                      }
                      onError={setError}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-2.5 p-4 sm:gap-4 sm:p-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5 sm:h-10 sm:w-10">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-xl font-bold leading-tight text-foreground sm:text-2xl">{value}</div>
          <div className="truncate text-sm font-medium text-foreground">{label}</div>
          <div className="truncate text-xs text-muted-foreground">{hint}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function RowActions({
  row,
  rooms,
  doctors,
  disabled = false,
  roomRequired = false,
  onCheckIn,
  onAssigned,
  onStart,
  onPickDoctor,
  onError,
}: {
  row: ReceptionRow;
  rooms: Room[];
  doctors: Array<{ id: string; name: string; specialty: string | null }>;
  // #18605: when viewing a future date the queue is read-only — every state-changing
  // action (phân công / check-in / gọi vào phòng / bắt đầu khám) is disabled.
  disabled?: boolean;
  /** #19665 — Phòng = Bắt buộc: no room on the row, no "Bắt đầu khám". */
  roomRequired?: boolean;
  onCheckIn: () => void;
  onAssigned: () => void;
  onStart: () => void;
  /** #19088 — có khi dòng này chọn được bác sĩ ngay ở quầy; mở ô chọn ở cột Bác sĩ. */
  onPickDoctor?: () => void;
  onError: (m: string) => void;
}) {
  // Điền sẵn phòng đã chọn lúc đặt lịch. Lễ tân không phải chọn lại thứ người đặt đã chọn, và
  // vẫn đổi được nếu cần (#18988).
  const [roomId, setRoomId] = useState(row.roomId ?? "");
  // The label of whatever is selected, for the trigger's `title` (#19033). Radix renders the
  // chosen item's children inside `SelectValue`, so the visible text follows the list — but the
  // trigger is narrow and a tooltip is the only place the full name always fits.
  const selectedRoomLabel = (() => {
    const r = rooms.find((x) => x.id === roomId);
    return r ? roomLabel(r) : "";
  })();
  const { operates: operatesEncounters } = useOperatesModule("ENCOUNTERS");
  const [assignOpen, setAssignOpen] = useState(false);
  const call = useMutation({
    mutationFn: () => assignRoom(row.appointmentId, roomId),
    onSuccess: onAssigned,
    onError: (e) => onError(apiError(e).detail),
  });

  // Booking Policy: lịch hẹn chưa phân công đủ → nút "Phân công" (mọi trạng thái).
  const needsAssign =
    row.assignState === "PENDING_DOCTOR" || row.assignState === "PENDING_ROOM";
  const assignBtn = needsAssign ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={disabled}
      onClick={() => setAssignOpen(true)}
    >
      Phân công
    </Button>
  ) : null;
  const assignModal = assignOpen ? (
    <AssignModal
      row={row}
      rooms={rooms}
      doctors={doctors}
      onClose={() => setAssignOpen(false)}
      onAssigned={() => {
        setAssignOpen(false);
        onAssigned();
      }}
      onError={onError}
    />
  ) : null;

  if (row.status === "REQUESTED" || row.status === "CONFIRMED") {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {assignBtn}
        <Button type="button" size="sm" disabled={disabled} onClick={onCheckIn}>
          Tiếp đón (check-in)
        </Button>
        {assignModal}
      </div>
    );
  }
  if (row.status === "CHECKED_IN") {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {assignBtn}
        {assignModal}
        <Button type="button" variant="ghost" size="sm" onClick={() => printTicket(row)}>
          <Printer />
          In số
        </Button>
        {/* #18988 — ô này giờ là PHÂN PHÒNG, không phải "gọi vào phòng".

            Từ quyết định sản phẩm 20/09/2026, backend cấp số thứ tự ngay khi lịch hẹn lần đầu có
            phòng — ở mọi kênh: đặt lịch, sửa lịch, phân công, chuyển khoa, tái khám, Mini App.
            Nên một lịch ĐÃ có phòng thì đã có số, và ô này không còn việc gì với nó.

            Điều kiện vì thế đọc `roomName`, không phải `roomNo`: thứ còn thiếu là PHÒNG. Giữ
            `roomNo == null` sẽ vẫn đúng với dữ liệu mới, nhưng nó nói sai lý do — và nó khiến
            một dòng dữ liệu cũ (có phòng, chưa có số) hiện một ô chọn phòng mà lễ tân không hiểu
            để làm gì. Với dòng cũ đó, chọn lại đúng phòng ấy sẽ cấp số: backend idempotent theo
            phòng, nên không có rủi ro đổi số. */}
        {rooms.length > 0 && row.roomName == null && (
          <>
            <Select value={roomId} onValueChange={setRoomId}>
              {/* #19033 — `w-[120px]` fits a code and nothing else, which is how this picker came
                  to show one. Widened so the name is the part that survives, and `title` carries
                  the whole label for the cases the ellipsis still eats. */}
              <SelectTrigger
                className="w-[16rem] max-w-full"
                aria-label="Phòng"
                title={selectedRoomLabel || undefined}
              >
                <SelectValue placeholder="— Phòng —" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {roomLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!roomId || call.isPending || disabled}
              onClick={() => call.mutate()}
            >
              Phân phòng
            </Button>
          </>
        )}
        {/* #18899 — an appointment with no doctor is a supported state ("Bác sĩ: Ẩn · Lễ tân
            xếp"), but an encounter cannot start without one. Refusing here says so before the
            request, instead of letting the server answer with a bare validation string. */}
        {/* ADR-0048 (#18965): phòng khám chế độ PATIENT_CHANNEL không vận hành Khám bệnh trong
            Addy — endpoint tạo lượt khám trả 409 cho họ. Màn Tiếp đón vẫn là của họ (đón bệnh
            nhân, phân phòng, cấp số), nhưng lượt khám thì nằm ở hệ thống chính. Ẩn hẳn nút thay
            vì để nó tạo ra một lượt khám trong Addy mà HIS thật không bao giờ biết — đó đúng là
            hình dạng lỗi ADR-0048 sinh ra để chặn. */}
        {/* #19088 — lý do bị khoá phải NHÌN THẤY được: `title` chỉ hiện khi rê chuột, nên quầy
            dùng tablet không bao giờ thấy. Chọn được ở quầy → nút nói luôn việc cần làm và bấm vào
            là mở ô chọn; không chọn được (chế độ tự xếp, hoặc không có quyền điều phối) → nút
            khoá kèm một dòng chữ nói phải làm ở đâu. */}
        {operatesEncounters && !row.doctorId && onPickDoctor && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onPickDoctor}
          >
            Cần chọn bác sĩ
          </Button>
        )}
        {operatesEncounters && !row.doctorId && !onPickDoctor && (
          <div className="flex flex-col items-start gap-0.5">
            <Button type="button" size="sm" disabled>
              Bắt đầu khám
              <ArrowRight />
            </Button>
            <span className="text-xs text-muted-foreground">
              Cần phân bác sĩ (Sửa lịch hẹn) trước khi khám
            </span>
          </div>
        )}
        {/* #19665 — Phòng = Bắt buộc: same lock as the doctor one, with the reason on screen
            (a tablet never shows `title`). The fix is the "Phân phòng" picker in this same cell. */}
        {operatesEncounters && row.doctorId && roomRequired && !hasRoom(row) && (
          <div className="flex flex-col items-start gap-0.5">
            <Button type="button" size="sm" disabled title={ROOM_REQUIRED_HINT}>
              Bắt đầu khám
              <ArrowRight />
            </Button>
            <span className="text-xs text-muted-foreground" data-testid="needs-room">
              {ROOM_REQUIRED_HINT}
            </span>
          </div>
        )}
        {operatesEncounters && row.doctorId && !(roomRequired && !hasRoom(row)) && (
          <Button type="button" size="sm" disabled={disabled} onClick={onStart}>
            Bắt đầu khám
            <ArrowRight />
          </Button>
        )}
      </div>
    );
  }
  if (needsAssign) {
    return (
      <div className="flex items-center gap-1.5">
        {assignBtn}
        {assignModal}
      </div>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

/**
 * Modal "Phân công" — lễ tân chọn bác sĩ và/hoặc phòng cho lịch hẹn đang chờ.
 * Gọi POST /appointments/{id}/assign; đóng + refetch khi thành công.
 */
function AssignModal({
  row,
  rooms,
  doctors,
  onClose,
  onAssigned,
  onError,
}: {
  row: ReceptionRow;
  rooms: Room[];
  doctors: Array<{ id: string; name: string; specialty: string | null }>;
  onClose: () => void;
  onAssigned: () => void;
  onError: (m: string) => void;
}) {
  const [doctorProfileId, setDoctorProfileId] = useState("");
  const [roomId, setRoomId] = useState("");

  const needDoctor = row.assignState === "PENDING_DOCTOR";
  const needRoom = row.assignState === "PENDING_ROOM";

  const submit = useMutation({
    mutationFn: () =>
      assign(row.appointmentId, {
        doctorProfileId: doctorProfileId || undefined,
        roomId: roomId || undefined,
      }),
    onSuccess: onAssigned,
    onError: (e) => onError(apiError(e).detail),
  });

  const canSubmit =
    (needDoctor && !!doctorProfileId) ||
    (needRoom && !!roomId) ||
    (!needDoctor && !needRoom && (!!doctorProfileId || !!roomId));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Phân công lịch hẹn"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4"
      onClick={onClose}
    >
      <Card
        className="grid w-[380px] max-w-[92vw] gap-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-foreground">Phân công lịch hẹn</h2>
        <p className="text-sm text-muted-foreground">
          {row.patientName}
          {row.patientCode ? ` · ${row.patientCode}` : ""}
        </p>

        {(needDoctor || (!needRoom && !needDoctor)) && (
          <div className="space-y-1.5">
            <Label htmlFor="assign-doctor">
              Bác sĩ{needDoctor ? " (bắt buộc)" : ""}
            </Label>
            <Select value={doctorProfileId} onValueChange={setDoctorProfileId}>
              <SelectTrigger id="assign-doctor">
                <SelectValue placeholder="— Chọn bác sĩ —" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                    {d.specialty ? ` · ${d.specialty}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {(needRoom || (!needRoom && !needDoctor)) && (
          <div className="space-y-1.5">
            <Label htmlFor="assign-room">
              Phòng{needRoom ? " (bắt buộc)" : ""}
            </Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger id="assign-room">
                <SelectValue placeholder="— Chọn phòng —" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {roomLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? "Đang lưu…" : "Phân công"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Open a minimal print window with the registration-number ticket.
 *
 * <p>Every interpolated field goes through {@link escapeHtml}: this writes into a same-origin
 * `about:blank` popup, so a patient name containing markup would otherwise run as script in our
 * own origin (#18623). The registration number and appointment time are escaped too — they are
 * server-derived today, but the rule that survives is "escape everything", not "escape the fields
 * someone judged risky".
 */
function printTicket(row: ReceptionRow) {
  const w = window.open("", "_blank", "width=320,height=480");
  if (!w) return;
  const time = new Date(row.startAt).toLocaleString("vi-VN");
  w.document.write(`
    <html><head><title>Phiếu số ${escapeHtml(row.registrationNo ?? "")}</title>
    <style>
      body{font-family:system-ui,sans-serif;text-align:center;padding:16px}
      .n{font-size:96px;font-weight:800;margin:8px 0}
      .lbl{font-size:14px;color:#555}
      .nm{font-size:18px;font-weight:600;margin-top:8px}
    </style></head><body>
      <div class="lbl">SỐ THỨ TỰ ĐĂNG KÝ</div>
      <div class="n">${escapeHtml(row.registrationNo ?? "—")}</div>
      <div class="nm">${escapeHtml(row.patientName)}</div>
      <div class="lbl">${escapeHtml(row.patientCode ?? "")}</div>
      <div class="lbl" style="margin-top:8px">Giờ hẹn: ${escapeHtml(time)}</div>
      ${row.roomName ? `<div class="lbl">Phòng: ${escapeHtml(row.roomName)}</div>` : ""}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}
